// SSE 스트리밍 유틸리티 함수 / SSE streaming utility functions
// POST 요청을 위한 커스텀 SSE 구현 / Custom SSE implementation for POST requests

export interface SSEOptions {
  method?: 'POST' | 'GET';
  headers?: Record<string, string>;
  body?: any;
  onMessage?: (data: string) => void;
  onError?: (error: Event) => void;
  onComplete?: () => void;
  onOpen?: () => void;
}

export interface SSEConnection {
  close: () => void;
  isConnected: boolean;
}

/**
 * POST 요청을 위한 SSE 스트리밍 연결 / SSE streaming connection for POST requests
 * EventSource는 GET만 지원하므로 fetch를 사용하여 구현
 * EventSource only supports GET, so we use fetch for implementation
 */
export const createSSEConnection = (
  url: string,
  options: SSEOptions = {}
): SSEConnection => {
  const {
    method = 'POST',
    headers = {},
    body,
    onMessage,
    onError,
    onComplete,
    onOpen,
  } = options;

  let isConnected = true;
  let abortController: AbortController | null = null;

  const close = () => {
    isConnected = false;
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  const connect = async () => {
    try {
      abortController = new AbortController();
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        let buffer = '';
        
        while (isConnected) {
          const { done, value } = await reader.read();
          
          if (done) {
            onComplete?.();
            break;
          }

          // 청크를 버퍼에 추가 / Add chunk to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          // 라인별로 처리 / Process line by line
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // 마지막 불완전한 라인은 버퍼에 보관
          
          for (const line of lines) {
            if (line.trim() === '') continue; // 빈 라인 무시
            
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              
              if (data === '[DONE]') {
                onComplete?.();
                return;
              }
              
              if (data.trim()) {
                try {
                  // JSON 파싱 시도 / Try to parse JSON
                  const parsedData = JSON.parse(data);
                  onMessage?.(parsedData);
                } catch {
                  // JSON이 아닌 경우 그대로 전달 / Pass as-is if not JSON
                  onMessage?.(data);
                }
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (isConnected) {
        console.error('SSE 연결 오류 / SSE connection error:', error);
        onError?.(error as Event);
      }
    }
  };

  // 비동기로 연결 시작 / Start connection asynchronously
  connect();

  return {
    close,
    get isConnected() {
      return isConnected;
    },
  };
};

/**
 * KPC 텍스트 생성 스트리밍 / KPC text generation streaming
 * @param prompt 텍스트 생성 프롬프트 / Text generation prompt
 * @param imageUrls 이미지 URL 배열 (선택사항) / Image URLs array (optional)
 * @param callbacks 콜백 함수들 / Callback functions
 * @returns SSE 연결 객체 / SSE connection object
 */
export const streamKpcTextGeneration = (
  prompt: string,
  imageUrls: string[] = [],
  callbacks: {
    onMessage?: (data: string) => void;
    onError?: (error: Event) => void;
    onComplete?: () => void;
    onOpen?: () => void;
  } = {}
): SSEConnection => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://ailic.kpc.or.kr';
  const url = `${baseUrl}/api/v2/kpc/text-generation/stream`;

  // 사용자 ID 가져오기 / Get user ID
  const getUserId = (): string | null => {
    try {
      // localStorage에서 사용자 정보 확인 / Check user info from localStorage
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        return user.id || null;
      }
      
      // URL 파라미터에서 확인 / Check from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const userIdFromUrl = urlParams.get('user');
      if (userIdFromUrl) {
        return userIdFromUrl;
      }
      
      return null;
    } catch (error) {
      console.error('사용자 ID 가져오기 실패 / Failed to get user ID:', error);
      return null;
    }
  };

  // expKey 가져오기 / Get expKey from URL params
  const getExpKey = (): string | null => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('expKey');
    } catch (error) {
      console.error('expKey 가져오기 실패 / Failed to get expKey:', error);
      return null;
    }
  };

  const userId = getUserId();
  const expKey = getExpKey();

  // 헤더 구성 / Build headers
  const headers: Record<string, string> = {};
  if (userId) {
    headers['userId'] = userId;
  }
  if (expKey) {
    headers['x-experiment-key'] = expKey;
  }

  return createSSEConnection(url, {
    method: 'POST',
    headers,
    body: {
      prompt,
      imageUrls,
    },
    ...callbacks,
  });
};

/**
 * 일반적인 SSE 연결 (GET 요청) / General SSE connection (GET request)
 * @param url SSE 엔드포인트 URL / SSE endpoint URL
 * @param callbacks 콜백 함수들 / Callback functions
 * @returns SSE 연결 객체 / SSE connection object
 */
export const createGetSSEConnection = (
  url: string,
  callbacks: {
    onMessage?: (data: string) => void;
    onError?: (error: Event) => void;
    onComplete?: () => void;
    onOpen?: () => void;
  } = {}
): SSEConnection => {
  return createSSEConnection(url, {
    method: 'GET',
    ...callbacks,
  });
};
